import { useCurrency } from "@/lib/currency";
import { useWholesaleAccount } from "@/hooks/useWholesaleAccount";
import { tierLabel } from "@/lib/wholesalePricing";

interface PriceDisplayProps {
  price: { amount: string; currencyCode?: string };
  compareAtPrice?: { amount: string; currencyCode?: string } | null;
  size?: "sm" | "lg";
  showCurrency?: boolean;
  className?: string;
  /** When true, do NOT apply the buyer's wholesale discount to this price. */
  ignoreWholesale?: boolean;
}

export const PriceDisplay = ({
  price,
  compareAtPrice,
  size = "sm",
  showCurrency = false,
  className = "",
  ignoreWholesale = false,
}: PriceDisplayProps) => {
  const { format, currency, isUsd } = useCurrency();
  const { account } = useWholesaleAccount();

  const retailSale = parseFloat(price.amount);
  const retailList = compareAtPrice ? parseFloat(compareAtPrice.amount) : null;

  // Hide price display for quote-only / wholesale-only variants (price = $0)
  if (retailSale <= 0) return null;

  const wholesaleActive =
    !ignoreWholesale && !!account && account.active && account.discount_percent > 0;

  // For wholesale: discount is taken off the original retail price (use list when on sale, else sale).
  const wholesaleBase = retailList ?? retailSale;
  const wholesaleSale = wholesaleActive
    ? Math.round((wholesaleBase * (1 - account!.discount_percent / 100)) * 100) / 100
    : retailSale;

  const sale = wholesaleActive ? wholesaleSale : retailSale;
  const list = wholesaleActive ? wholesaleBase : retailList;
  const onSale = list !== null && list > sale;
  const savings = onSale ? list! - sale : 0;
  const percentOff = onSale ? Math.round((savings / list!) * 100) : 0;

  const isLg = size === "lg";

  if (!onSale) {
    return (
      <span
        className={`${isLg ? "text-2xl" : "text-lg"} font-semibold text-primary ${className}`}
      >
        {format(sale)}
        {showCurrency && (
          <span className="text-sm text-muted-foreground ml-2">{currency}</span>
        )}
      </span>
    );
  }

  return (
    <div className={`flex flex-wrap items-baseline gap-x-2 gap-y-1 ${className}`}>
      <span className={`${isLg ? "text-2xl" : "text-lg"} font-bold text-primary`}>
        {format(sale)}
      </span>
      <span className={`${isLg ? "text-base" : "text-sm"} text-muted-foreground line-through`}>
        {format(list!)}
      </span>
      <span
        className={`${isLg ? "text-sm" : "text-xs"} font-semibold text-destructive`}
      >
        {wholesaleActive
          ? `${tierLabel(account?.tier)} wholesale · ${percentOff}% off`
          : `${percentOff}% off · Save ${format(savings)}`}
      </span>
      {showCurrency && !isUsd && (
        <span className="text-xs text-muted-foreground">{currency}</span>
      )}
    </div>
  );
};
